"use client";

import type { CalendarTodayResponse } from "@/lib/api/calendar";
import { formatDateLong, formatDayMonth, typeLabel } from "./types";
import Badge from "@ishvaram/ui/Badge";

/** A month row the card adds to /calendar/today (the calendar's merged rows). */
interface CalendarTodayExtra {
  id: string;
  name_en: string;
  name_hi?: string;
  type: string;
  date: string;
  fasting?: boolean;
  fasting_date?: string | null;
}

interface CalendarTodayCardProps {
  data: CalendarTodayResponse;
  /**
   * Today's rows the calendar holds beyond /calendar/today (which reads the
   * festival list only): the vrat-year Purnima, Amavasya, Masik Shivratri and
   * Sankranti rows, so the card agrees with the answer box below it.
   */
  extraEvents?: readonly CalendarTodayExtra[];
  /** Rows dated another day whose fast the engine keeps today. */
  fastsKept?: readonly CalendarTodayExtra[];
  /** Today is a fasting day by either source; defaults to the API's flag. */
  isFastingDay?: boolean;
}

const NO_EXTRAS: readonly CalendarTodayExtra[] = [];

function extraFastsToday(event: CalendarTodayExtra, today: string): boolean {
  return event.fasting === true && (event.fasting_date || event.date) === today;
}

export function CalendarTodayCard({
  data,
  extraEvents = NO_EXTRAS,
  fastsKept = NO_EXTRAS,
  isFastingDay = data.is_fasting_day,
}: CalendarTodayCardProps) {
  const allEvents = [
    ...data.festivals,
    ...data.vrats,
    ...data.eclipses,
    ...data.tithi_events,
    ...extraEvents.map((event) => ({
      ...event,
      fasting: extraFastsToday(event, data.date),
      note:
        event.fasting && !extraFastsToday(event, data.date) && event.fasting_date
          ? `Fast kept on ${formatDayMonth(event.fasting_date)}`
          : null,
    })),
    ...fastsKept.map((event) => ({
      ...event,
      id: `kept-${event.id}`,
      fasting: true,
      note: `Fast kept today (listed on ${formatDayMonth(event.date)})`,
    })),
  ];

  return (
    <div className="bg-gradient-to-br from-ish-accent-light/30 to-ish-accent-light/10 border border-ish-accent/20 rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-2 h-2 rounded-full bg-ish-accent shrink-0" />
        <h2 className="font-serif text-lg font-bold text-ish-dark m-0">
          Today&apos;s Significance
        </h2>
        <span className="text-sm text-ish-text-muted">{formatDateLong(data.date)}</span>
      </div>

      {allEvents.length === 0 ? (
        <p className="text-sm text-ish-text-muted m-0">
          No special observances today. Check the monthly calendar below for upcoming festivals.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {allEvents.map((event) => (
            <div
              key={event.id}
              className="flex items-center gap-2.5 p-2 px-3 bg-white/60 rounded-lg border border-ish-border/10"
            >
              <Badge
                label={typeLabel(event.type)}
                variant={
                  event.type === "vrat"
                    ? "success"
                    : event.type === "festival"
                      ? "warning"
                      : "accent"
                }
              />
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-sm text-ish-dark">{event.name_en}</span>
                <span className="text-sm text-ish-text-muted ml-1.5">{event.name_hi}</span>
                {"note" in event && event.note ? (
                  <span className="block text-xs text-ish-text-muted">{event.note}</span>
                ) : null}
              </div>
              {event.fasting && <Badge label="Fasting" variant="error" />}
            </div>
          ))}
        </div>
      )}

      {isFastingDay && (
        <div className="mt-3 p-2 px-3 bg-red-50 rounded-lg text-sm text-red-600 font-medium border border-red-100">
          Today is a fasting day. Avoid grains, beans, and non-vegetarian food if observing the
          vrat.
        </div>
      )}
    </div>
  );
}
